import React, { useState } from 'react';
import API from '../../../api/api';
import { ClipPrompt } from '../../../api/structs/clip';
import { TextArea } from '../../ui';
import { useToast } from '../../../hooks/useToast';
import { constructMediaUrl } from '../../../api/helpers';
import NarrationModal from '../../modals/NarrationModal';
import { narratorTakeItem } from '../../modals/narrationFit';
import { useCostDisplay } from '../../../hooks/useCostDisplay';

interface NarratorTrackProps {
  clipId: string;
  clip: ClipPrompt;
  onRefresh: () => void;
}

const NarratorTrack: React.FC<NarratorTrackProps> = ({ clipId, clip, onRefresh }) => {
  const toast = useToast();
  const display = useCostDisplay();
  const showSubtitles = Boolean(clip.metadata?.show_subtitles);
  const [subtitleText, setSubtitleText] = useState<string>(
    String(clip.metadata?.subtitleText ?? '')
  );
  const [showModal, setShowModal] = useState(false);

  // Bound id first, then the render path's own fallback: the first audio item
  // that says it is narration. Without the fallback an auto-wired narration —
  // a pipeline take, or one whose binding a restart lost — was invisible here,
  // and so could not be removed. The narration modal measures the same take, so
  // the resolution lives in one place.
  const narratorItem = narratorTakeItem(clip);
  const narratorAudioUrl = narratorItem?.file_url ? constructMediaUrl(narratorItem.file_url) : null;
  const isNarratorReady = narratorAudioUrl
    && !narratorAudioUrl.includes('_waiting')
    && !narratorAudioUrl.includes('_failed');
  const isNarratorPending = narratorItem && !isNarratorReady;

  const handleToggle = async () => {
    try {
      await API.editClipMetadata(clipId, 'show_subtitles', !showSubtitles);
      onRefresh();
    } catch (error: any) {
      toast({ text: `Failed to toggle subtitles: ${error.message}`, level: 'error' });
    }
  };

  const handleBlur = async () => {
    try {
      await API.editClipMetadata(clipId, 'subtitleText', subtitleText);
      onRefresh();
    } catch (error: any) {
      toast({ text: `Failed to save subtitles: ${error.message}`, level: 'error' });
    }
  };

  // One backend call: it detaches the narration item AND clears the key. A take
  // that is still generating (or that failed) is removable too — an unfinished
  // audio item on the clip is exactly what blocks the clip from rendering.
  const handleRemoveNarration = async () => {
    try {
      await API.deleteClipAudio(clipId, 'narrator');
      onRefresh();
    } catch (error: any) {
      toast({ text: `Failed to remove narration: ${error.message}`, level: 'error' });
    }
  };

  const removeButton = (
    <button
      onClick={handleRemoveNarration}
      className="text-zinc-500 hover:text-white font-mono text-sm transition-colors shrink-0"
      title="Remove narration"
    >✕</button>
  );

  return (
    <>
      {/* Narration section */}
      <div className="mb-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-mono">Narrator Voice</span>
          <button
            onClick={() => setShowModal(true)}
            className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.15em] font-mono text-zinc-500 hover:text-white border border-white/10 hover:border-white/30 px-2 py-1 transition-colors"
          >
            {narratorItem ? 'Regenerate' : '+ Create Narration'}
            {display === 'credits' && (
              <span className="text-yellow-300 font-bold normal-case tracking-normal">⚡</span>
            )}
          </button>
        </div>

        {isNarratorReady && narratorAudioUrl ? (
          <div className="flex items-center gap-3">
            <audio controls preload="none" src={narratorAudioUrl} className="flex-1 h-8 min-w-0" />
            {removeButton}
          </div>
        ) : isNarratorPending ? (
          <div className="flex items-center gap-3">
            <p className="flex-1 text-[10px] text-zinc-600 font-mono animate-pulse">generating narration...</p>
            {removeButton}
          </div>
        ) : (
          <p className="text-[10px] text-zinc-700 font-mono">no narration · click to generate</p>
        )}
      </div>

      {/* Subtitles section */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <span className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-mono">Narrator Subtitles</span>
          <button
            onClick={handleToggle}
            className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer border transition-colors ${
              showSubtitles ? 'bg-white border-white' : 'bg-transparent border-white/20'
            }`}
            aria-label="Toggle subtitles"
          >
            <span
              className={`pointer-events-none inline-block h-4 w-4 mt-[1px] transform transition-transform ${
                showSubtitles ? 'translate-x-[18px] bg-black' : 'translate-x-[1px] bg-zinc-600'
              }`}
            />
          </button>
        </div>

        {showSubtitles && (
          <TextArea
            value={subtitleText}
            onChange={(e) => setSubtitleText(e.target.value)}
            onBlur={handleBlur}
            placeholder="Narrator subtitle text..."
            rows={3}
          />
        )}
      </div>

      <NarrationModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        clipId={clipId}
        clip={clip}
        onSuccess={onRefresh}
      />
    </>
  );
};

export default NarratorTrack;
